import { apiGet, apiPut, apiDelete, apiPost, getAccessToken } from './api';
import API_CONFIG from '../config/api';

/**
 * Obtiene un usuario por RUT (solo admin).
 */
export const getUsuario = async (rut) => {
  const response = await apiGet(`${API_CONFIG.ENDPOINTS.ADMIN.USUARIOS}/${rut}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Error al obtener usuario');
  return data.data;
};

/**
 * Historial deportivo del usuario (solo admin): ranking vigente (DOTS y lugar) y últimos eventos.
 */
export const getHistorialUsuario = async (rut) => {
  const response = await apiGet(`${API_CONFIG.ENDPOINTS.ADMIN.USUARIOS}/${rut}/historial`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Error al obtener el historial');
  return data.data;
};

/**
 * Crea una afiliación para un usuario (solo admin). Envía correo al usuario con el número de afiliación.
 */
export const crearAfiliacionAdmin = async (rut, tipo_afiliacion = 'anual') => {
  const response = await apiPost(API_CONFIG.ENDPOINTS.AFILIACION.CREAR, {
    usuario_id: rut,
    tipo_afiliacion
  });
  const data = await response.json();
  if (!data.ok) throw new Error(data.mensaje || data.error || 'Error al afiliar');
  return data;
};

// Valores de afiliación (open/junior, con/sin club)
export const getValoresAfiliacion = async () => {
  const response = await apiGet(API_CONFIG.ENDPOINTS.ADMIN.VALORES_AFILIACION);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Error al obtener valores de afiliación');
  return data;
};

export const putValoresAfiliacion = async (payload) => {
  const response = await apiPut(API_CONFIG.ENDPOINTS.ADMIN.VALORES_AFILIACION, payload);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Error al actualizar valores');
  return data;
};

export const getUsuarios = async ({ page = 1, perPage = 20, search = '', estado = '' } = {}) => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('per_page', perPage);
  if (search) params.append('search', search);
  if (estado) params.append('estado', estado);

  const response = await apiGet(
    `${API_CONFIG.ENDPOINTS.ADMIN.USUARIOS}?${params.toString()}`
  );

  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Error al obtener usuarios');

  return data;
};

export const actualizarUsuarioEstado = async (rut, estado) => {
  return actualizarUsuario(rut, { estado });
};

/**
 * Actualiza usuario (estado, perfiles y/o datos personales).
 * @param {string} rut - RUT del usuario
 * @param {{ estado?: string, perfiles?: string[], nombre?: string, apellido?: string, email?: string, telefono?: string, fecha_nacimiento?: string, genero?: string, ciudad?: string, region?: string, direccion?: string, pais?: string }} payload
 */
export const actualizarUsuario = async (rut, payload) => {
  const response = await apiPut(
    `${API_CONFIG.ENDPOINTS.ADMIN.USUARIOS}/${rut}`,
    payload
  );

  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Error al actualizar usuario');

  return data;
};

/**
 * Banea un usuario con motivo, período y genera carta PDF (descarga en el navegador).
 * El envío por correo es aparte: enviarCartaBaneo().
 * @param {string} rut
 * @param {{ motivo: string, ban_permanente?: boolean, fecha_fin?: string }} payload
 */
export const banearUsuario = async (rut, payload) => {
  const parse = async (response) => {
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Error al banear usuario');
    return data;
  };
  // POST fijo /usuarios/banear (rut en JSON): evita 404 si el proxy no enruta .../usuarios/<rut>/banear
  try {
    const res = await apiPost(`${API_CONFIG.ENDPOINTS.ADMIN.USUARIOS}/banear`, {
      rut,
      ...payload
    });
    return parse(res);
  } catch (e) {
    const m = ((e && e.message) || '').toLowerCase();
    if (
      m.includes('recurso solicitado')
      || m.includes('no existe')
      || m.includes('not found')
    ) {
      const res = await apiPost(
        `${API_CONFIG.ENDPOINTS.ADMIN.USUARIOS}/${encodeURIComponent(rut)}/banear`,
        payload
      );
      return parse(res);
    }
    throw e;
  }
};

/**
 * Envía por correo la carta PDF del baneo activo (mismo PDF que la descarga).
 */
export const enviarCartaBaneo = async (rut) => {
  const response = await apiPost(
    `${API_CONFIG.ENDPOINTS.ADMIN.USUARIOS}/${rut}/enviar-carta-baneo`,
    {}
  );
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Error al enviar la carta');
  return data;
};

/**
 * Abre en una nueva pestaña el PDF de la carta del baneo activo (GET con JWT).
 */
export const verCartaBaneoPdf = async (rut) => {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ADMIN.USUARIOS}/${rut}/carta-baneo`;
  const token = getAccessToken();
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    cache: 'no-cache',
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = 'No se pudo cargar la carta';
    try {
      const j = JSON.parse(text);
      msg = j.error || j.message || msg;
    } catch (_) {
      if (text) msg = text.slice(0, 200);
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const w = window.open(objectUrl, '_blank', 'noopener,noreferrer');
  if (!w) {
    URL.revokeObjectURL(objectUrl);
    throw new Error('El navegador bloqueó la ventana emergente');
  }
  setTimeout(() => URL.revokeObjectURL(objectUrl), 120000);
};

/**
 * Elimina un usuario (solo admin). Puede fallar si tiene datos asociados.
 */
export const deleteUsuario = async (rut) => {
  const response = await apiDelete(
    `${API_CONFIG.ENDPOINTS.ADMIN.USUARIOS}/${rut}`
  );
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Error al borrar usuario');
  return data;
};

/**
 * Reenvía el correo de verificación de email al usuario (solo admin).
 */
export const reenviarCorreoVerificacion = async (rut) => {
  const response = await apiPost(
    `${API_CONFIG.ENDPOINTS.ADMIN.USUARIOS}/${rut}/reenviar-verificacion-email`,
    {}
  );
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Error al reenviar correo');
  return data;
};

/**
 * Asocia un usuario a un club (admin sin restricciones de club).
 * Mantiene 1 solo club activo por usuario (otros activos -> inactivo).
 */
export const asociarUsuarioAClubAdmin = async (rut, club_id) => {
  const response = await apiPost(API_CONFIG.ENDPOINTS.ADMIN.MIEMBROS_CLUB.ASOCIAR, {
    rut,
    club_id
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Error al asociar usuario a club');
  return data;
};

/**
 * Quita membresía activa del usuario (admin).
 * Deja al usuario sin club activo.
 */
export const quitarMembresiaClubAdmin = async (rut) => {
  const response = await apiPost(API_CONFIG.ENDPOINTS.ADMIN.MIEMBROS_CLUB.QUITAR, {
    rut
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Error al quitar membresía');
  return data;
};
